import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  useCreateSendPoolMutation,
  useGetDomainsQuery,
  useGetSendPoolsQuery,
  useGetUserPreferencesQuery,
  useLazyGetDomainRoutingPolicyQuery,
  useLazyGetPoolCredentialsQuery,
  usePutPoolCredentialsMutation,
  useUpdateSendPoolMutation,
} from '../services/api';
import EMMSectionHeader from './EMMSectionHeader';
import HelpSidebar from './HelpSidebar';
import {
  getTenantTimezoneFromPreferencesData,
  toLocaleStringInTimezone,
} from '../utils/tenantTimezone';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';

type RelayProvider = 'MAILCHANNELS' | 'SMTP_COM' | 'MAILGUN' | 'SENDGRID' | 'ELASTIC_EMAIL';
type RelayPurpose = 'cold_outreach' | 'transactional' | 'newsletter';

type PoolUsageRecord = {
  domainCount: number;
  domains: string[];
};

type PoolCredentialsRecord = {
  configured: boolean;
  lastUpdatedAt: string | null;
  channel: string | null;
};

const RELAY_PROVIDER_SET = new Set<string>(['MAILCHANNELS', 'SMTP_COM', 'MAILGUN', 'SENDGRID', 'ELASTIC_EMAIL']);
const RELAY_ACCOUNT_SUFFIX_PATTERN = /^[A-Za-z0-9]{4}$/;

function normalizeRelaySuffixInput(value: string): string {
  return String(value || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 4);
}

function providerLabel(providerType: string): string {
  const pt = String(providerType || '').toUpperCase();
  if (pt === 'MAILCHANNELS') return 'MailChannels';
  if (pt === 'SMTP_COM') return 'SMTP.com';
  if (pt === 'MAILGUN') return 'Mailgun';
  if (pt === 'SENDGRID') return 'Twilio SendGrid';
  if (pt === 'ELASTIC_EMAIL') return 'Elastic Email';
  return pt || 'Unknown';
}

function relayAlias(providerType: string, suffix: string): string {
  const provider = providerLabel(providerType);
  const normalizedSuffix = String(suffix || '').trim();
  return RELAY_ACCOUNT_SUFFIX_PATTERN.test(normalizedSuffix) ? `${provider} ${normalizedSuffix}` : provider;
}

function extractRelaySuffix(pool: any): string {
  const direct = String(pool?.relayAccountSuffix || '').trim();
  if (RELAY_ACCOUNT_SUFFIX_PATTERN.test(direct)) return direct;
  const name = String(pool?.name || '');
  const fallbackMatch = name.match(/\[([A-Za-z0-9]{4})\]\s*$/);
  return fallbackMatch?.[1] || '';
}

function displayPoolName(name: string): string {
  return String(name || '').replace(/\s*\[[A-Za-z0-9]{4}\]\s*$/, '').trim();
}

function normalizeRelayProvider(value: string): RelayProvider {
  const normalized = String(value || '').toUpperCase();
  if (normalized === 'MAILCHANNELS') return 'MAILCHANNELS';
  if (normalized === 'MAILGUN') return 'MAILGUN';
  if (normalized === 'SENDGRID' || normalized === 'TWILIO' || normalized === 'TWILIO_SENDGRID') return 'SENDGRID';
  if (normalized === 'ELASTIC_EMAIL' || normalized === 'ELASTICEMAIL') return 'ELASTIC_EMAIL';
  return 'SMTP_COM';
}

function relayCredentialUsernameLabel(provider: string): string {
  const normalized = normalizeRelayProvider(provider);
  if (normalized === 'MAILCHANNELS') return 'SMTP username (Account ID)';
  if (normalized === 'MAILGUN') return 'SMTP login (email)';
  if (normalized === 'ELASTIC_EMAIL') return 'SMTP username';
  if (normalized === 'SENDGRID') return 'SMTP username';
  return 'Channel login (email)';
}

function relayCredentialPasswordLabel(provider: string): string {
  const normalized = normalizeRelayProvider(provider);
  if (normalized === 'MAILGUN') return 'SMTP password';
  if (normalized === 'MAILCHANNELS') return 'SMTP password';
  if (normalized === 'ELASTIC_EMAIL') return 'SMTP password';
  if (normalized === 'SENDGRID') return 'SMTP password (API key)';
  return 'Channel password';
}

function relayCredentialUsernamePlaceholder(provider: string): string {
  const normalized = normalizeRelayProvider(provider);
  if (normalized === 'MAILCHANNELS') return 'e.g. ee9aefhbcj99';
  if (normalized === 'MAILGUN') return 'e.g. postmaster@mg.yourdomain.com';
  if (normalized === 'ELASTIC_EMAIL') return 'e.g. your-smtp-username';
  if (normalized === 'SENDGRID') return 'Usually: apikey';
  return 'e.g. admin@yourdomain.com';
}

function relayCredentialPasswordPlaceholder(provider: string): string {
  const normalized = normalizeRelayProvider(provider);
  if (normalized === 'MAILGUN') return 'Paste Mailgun SMTP password';
  if (normalized === 'MAILCHANNELS') return 'Paste MailChannels SMTP password';
  if (normalized === 'ELASTIC_EMAIL') return 'Paste Elastic Email SMTP password';
  if (normalized === 'SENDGRID') return 'Paste Twilio SendGrid API key';
  return 'Paste channel password';
}

function relayCredentialHint(provider: string): string {
  const normalized = normalizeRelayProvider(provider);
  if (normalized === 'MAILCHANNELS') {
    return 'For MailChannels, username must be Account ID (not an email).';
  }
  if (normalized === 'MAILGUN') {
    return 'Use Mailgun SMTP login + SMTP password for this relay account.';
  }
  if (normalized === 'ELASTIC_EMAIL') {
    return 'Use Elastic Email SMTP username + SMTP password for this relay account.';
  }
  if (normalized === 'SENDGRID') {
    return 'Use Twilio SendGrid SMTP credentials (commonly username "apikey" + API key password).';
  }
  return 'Use SMTP.com Channel login + Channel password for this relay account.';
}

function providerUsesPoolLevelCredentials(provider: string): boolean {
  const normalized = normalizeRelayProvider(provider);
  return normalized === 'MAILCHANNELS' || normalized === 'SENDGRID' || normalized === 'ELASTIC_EMAIL';
}

function providerCredentialScopeText(provider: string): string {
  const normalized = normalizeRelayProvider(provider);
  if (normalized === 'SMTP_COM') {
    return 'SMTP.com credentials are configured per domain in Domains -> Outbound because each domain/channel has its own login/password.';
  }
  if (normalized === 'MAILGUN') {
    return 'Mailgun credentials are configured per domain in Domains -> Outbound.';
  }
  if (normalized === 'ELASTIC_EMAIL') {
    return 'Elastic Email credentials are configured once per relay account here.';
  }
  if (normalized === 'SENDGRID') {
    return 'Twilio SendGrid credentials are configured once per relay account here.';
  }
  return 'Credentials are shared at relay-account level for this provider.';
}

function isSendgridProvider(provider: string): boolean {
  return normalizeRelayProvider(provider) === 'SENDGRID';
}

function healthVariant(state?: string): 'success' | 'warning' | 'danger' | 'secondary' {
  if (state === 'GREEN') return 'success';
  if (state === 'YELLOW') return 'warning';
  if (state === 'RED') return 'danger';
  return 'secondary';
}

function purposeLabel(pool: any): string {
  const allowed = Array.isArray(pool?.policy?.allowedMessageClasses)
    ? pool.policy.allowedMessageClasses
    : [];
  const hasCold = allowed.includes('cold_outreach');
  const hasTransactional = allowed.includes('transactional');
  const hasNewsletter = allowed.includes('newsletter');
  if (hasCold && !hasTransactional && !hasNewsletter) return 'Cold Outreach';
  if (!hasCold && hasTransactional && hasNewsletter) return 'Transactional + Newsletter';
  if (!hasCold && hasTransactional && !hasNewsletter) return 'Transactional';
  if (!hasCold && !hasTransactional && hasNewsletter) return 'Newsletter';
  return allowed.length > 0 ? allowed.join(', ') : 'Unspecified';
}

function purposeFromPool(pool: any): RelayPurpose {
  const allowed = Array.isArray(pool?.policy?.allowedMessageClasses)
    ? pool.policy.allowedMessageClasses
    : [];
  const hasCold = allowed.includes('cold_outreach');
  const hasTransactional = allowed.includes('transactional');
  const hasNewsletter = allowed.includes('newsletter');
  if (hasCold && !hasTransactional && !hasNewsletter) return 'cold_outreach';
  if (hasNewsletter && !hasTransactional) return 'newsletter';
  return 'transactional';
}

function relayPurposeToApiPurpose(purpose: RelayPurpose): 'cold_outreach' | 'business_emails' {
  return purpose === 'cold_outreach' ? 'cold_outreach' : 'business_emails';
}

export default function RelayAccountsAdmin() {
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [showHelpSidebar, setShowHelpSidebar] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createProvider, setCreateProvider] = useState<RelayProvider>('MAILCHANNELS');
  const [createPurpose, setCreatePurpose] = useState<RelayPurpose>('cold_outreach');
  const [createSuffix, setCreateSuffix] = useState('');
  const [createCredentialUsername, setCreateCredentialUsername] = useState('');
  const [createCredentialPassword, setCreateCredentialPassword] = useState('');

  const [editingPoolId, setEditingPoolId] = useState<string | null>(null);
  const [editingProvider, setEditingProvider] = useState<RelayProvider>('MAILCHANNELS');
  const [editName, setEditName] = useState('');
  const [editPurpose, setEditPurpose] = useState<RelayPurpose>('cold_outreach');
  const [editSuffix, setEditSuffix] = useState('');
  const [editCredentialUsername, setEditCredentialUsername] = useState('');
  const [editCredentialPassword, setEditCredentialPassword] = useState('');

  const [usageByPoolId, setUsageByPoolId] = useState<Record<string, PoolUsageRecord>>({});
  const [credentialsByPoolId, setCredentialsByPoolId] = useState<Record<string, PoolCredentialsRecord>>({});

  const {
    data: sendPoolsData,
    error: sendPoolsError,
    isLoading: isLoadingPools,
    refetch: refreshPools,
  } = useGetSendPoolsQuery(undefined);
  const { data: domainsData } = useGetDomainsQuery(undefined);
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );

  const [fetchRouting] = useLazyGetDomainRoutingPolicyQuery();
  const [fetchPoolCredentials] = useLazyGetPoolCredentialsQuery();
  const [createSendPool, { isLoading: isCreatingPool }] = useCreateSendPoolMutation();
  const [updateSendPool, { isLoading: isUpdatingPool }] = useUpdateSendPoolMutation();
  const [putPoolCredentials, { isLoading: isSavingRelayCredentials }] = usePutPoolCredentialsMutation();

  useEffect(() => {
    if (providerUsesPoolLevelCredentials(createProvider)) return;
    setCreateCredentialUsername('');
    setCreateCredentialPassword('');
  }, [createProvider]);

  useEffect(() => {
    if (!sendPoolsError) return;
    const msg =
      (sendPoolsError as any)?.data?.message ||
      (sendPoolsError as any)?.error ||
      'Failed to load relay accounts';
    setError(String(msg));
  }, [sendPoolsError]);

  const pools = useMemo(() => (sendPoolsData as any)?.data?.pools || [], [sendPoolsData]);

  const relayPools = useMemo(() => {
    const list = (Array.isArray(pools) ? pools : []).filter((pool: any) => {
      const pt = String(pool?.providerType || '').toUpperCase();
      return RELAY_PROVIDER_SET.has(pt);
    });
    list.sort((a: any, b: any) => {
      const aProvider = providerLabel(a.providerType);
      const bProvider = providerLabel(b.providerType);
      if (aProvider !== bProvider) return aProvider.localeCompare(bProvider);
      const aSuffix = extractRelaySuffix(a);
      const bSuffix = extractRelaySuffix(b);
      if (aSuffix !== bSuffix) return aSuffix.localeCompare(bSuffix);
      return String(a.name || a.poolId || '').localeCompare(String(b.name || b.poolId || ''));
    });
    return list;
  }, [pools]);

  const relayPoolIds = useMemo(
    () => relayPools.map((pool: any) => String(pool.poolId || '')).filter(Boolean),
    [relayPools]
  );
  const relayPoolIdsKey = useMemo(() => relayPoolIds.join(','), [relayPoolIds]);

  const domainNames = useMemo(() => {
    const raw = (domainsData as any)?.data?.domains ?? (domainsData as any)?.domains ?? [];
    return (Array.isArray(raw) ? raw : [])
      .map((item: any) => String(item?.domain || item || '').trim().toLowerCase())
      .filter(Boolean);
  }, [domainsData]);
  const domainNamesKey = useMemo(() => domainNames.join(','), [domainNames]);

  const refreshUsage = useCallback(async () => {
    if (relayPoolIds.length === 0) {
      setUsageByPoolId({});
      return;
    }

    const validPoolIds = new Set(relayPoolIds);
    if (domainNames.length === 0) {
      const emptyMap: Record<string, PoolUsageRecord> = {};
      relayPoolIds.forEach((poolId) => {
        emptyMap[poolId] = { domainCount: 0, domains: [] };
      });
      setUsageByPoolId(emptyMap);
      return;
    }

    const usagesByPool = new Map<string, Set<string>>();
    relayPoolIds.forEach((poolId) => usagesByPool.set(poolId, new Set<string>()));

    const responses = await Promise.all(
      domainNames.map(async (domain) => {
        try {
          const result: any = await fetchRouting(domain).unwrap();
          return { domain, policy: result?.data?.policy || result?.policy || null };
        } catch {
          return { domain, policy: null };
        }
      })
    );

    responses.forEach(({ domain, policy }) => {
      if (!policy) return;
      const referencedPoolIds = new Set<string>();
      const forcedPoolId = String(policy?.overrides?.forcePoolId || '').trim();
      if (forcedPoolId) referencedPoolIds.add(forcedPoolId);

      const routes = policy?.routes && typeof policy.routes === 'object' ? policy.routes : {};
      Object.values(routes as Record<string, any>).forEach((route: any) => {
        const primary = String(route?.primaryPoolId || '').trim();
        if (primary) referencedPoolIds.add(primary);
        const failovers = Array.isArray(route?.failoverPoolIds) ? route.failoverPoolIds : [];
        failovers.forEach((poolId: string) => {
          const normalized = String(poolId || '').trim();
          if (normalized) referencedPoolIds.add(normalized);
        });
      });

      referencedPoolIds.forEach((poolId) => {
        if (!validPoolIds.has(poolId)) return;
        const set = usagesByPool.get(poolId);
        if (!set) return;
        set.add(domain);
      });
    });

    const next: Record<string, PoolUsageRecord> = {};
    relayPoolIds.forEach((poolId) => {
      const domains = Array.from(usagesByPool.get(poolId) || []).sort((a, b) => a.localeCompare(b));
      next[poolId] = {
        domainCount: domains.length,
        domains,
      };
    });
    setUsageByPoolId(next);
  }, [domainNames, fetchRouting, relayPoolIds]);

  const refreshCredentials = useCallback(async () => {
    if (relayPoolIds.length === 0) {
      setCredentialsByPoolId({});
      return;
    }

    const results = await Promise.all(
      relayPoolIds.map(async (poolId) => {
        try {
          const response: any = await fetchPoolCredentials(poolId).unwrap();
          const payload = response?.data || response || {};
          return {
            poolId,
            configured: Boolean(payload?.configured),
            lastUpdatedAt: payload?.lastUpdatedAt ? String(payload.lastUpdatedAt) : null,
            channel: payload?.channel ? String(payload.channel) : null,
          };
        } catch {
          return {
            poolId,
            configured: false,
            lastUpdatedAt: null,
            channel: null,
          };
        }
      })
    );

    const next: Record<string, PoolCredentialsRecord> = {};
    results.forEach((item) => {
      next[item.poolId] = {
        configured: item.configured,
        lastUpdatedAt: item.lastUpdatedAt,
        channel: item.channel,
      };
    });
    setCredentialsByPoolId(next);
  }, [fetchPoolCredentials, relayPoolIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refreshUsage();
      if (cancelled) return;
      await refreshCredentials();
    })().catch(() => {
      if (!cancelled) {
        setError('Could not load relay account details.');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [domainNamesKey, relayPoolIdsKey, refreshCredentials, refreshUsage]);

  function openCreateModal() {
    setCreateName('');
    setCreateProvider('MAILCHANNELS');
    setCreatePurpose('cold_outreach');
    setCreateSuffix('');
    setCreateCredentialUsername('');
    setCreateCredentialPassword('');
    setModalError(null);
    setError(null);
    setShowCreateModal(true);
  }

  async function submitCreateRelayAccount() {
    const name = String(createName || '').trim();
    const suffix = String(createSuffix || '').trim();
    const credentialUsername = String(createCredentialUsername || '').trim();
    const credentialPassword = String(createCredentialPassword || '').trim();
    const usePoolCredentials = providerUsesPoolLevelCredentials(createProvider);
    if (!name) {
      setModalError('Relay account name is required.');
      return;
    }
    if (suffix && !RELAY_ACCOUNT_SUFFIX_PATTERN.test(suffix)) {
      setModalError('Relay account suffix must be exactly 4 letters/numbers (example: 083c).');
      return;
    }

    setModalError(null);
    setError(null);
    try {
      const purposeForApi = relayPurposeToApiPurpose(createPurpose);
      const createRes: any = await createSendPool({
        name,
        providerType: createProvider,
        purpose: purposeForApi,
        relayAccountSuffix: suffix || undefined,
      }).unwrap();
      const createPayload = createRes?.data || createRes || {};
      const createdPoolId = String(createPayload?.poolId || '').trim();
      const createdSuffix = String(createPayload?.relayAccountSuffix || '').trim();
      const backendReturnedSuffix = RELAY_ACCOUNT_SUFFIX_PATTERN.test(createdSuffix);
      // Back-compat fallback for stacks where relayAccountSuffix is not yet supported on pool meta.
      if (suffix && createdPoolId && !backendReturnedSuffix) {
        await updateSendPool({
          poolId: createdPoolId,
          name: `${name} [${suffix}]`,
        }).unwrap();
      }
      if (usePoolCredentials && createdPoolId && (credentialUsername || credentialPassword)) {
        try {
          await putPoolCredentials({
            poolId: createdPoolId,
            username: credentialUsername || undefined,
            password: credentialPassword || undefined,
          }).unwrap();
        } catch (credErr: any) {
          const credMsg =
            credErr?.data?.message ||
            credErr?.message ||
            credErr?.error ||
            'Credential save failed';
          setShowCreateModal(false);
          setModalError(null);
          setError(
            `Relay account was created, but credentials failed to save (${credMsg}). Open Edit on this relay and retry.`
          );
          await refreshPools();
          await refreshCredentials();
          return;
        }
      }
      setModalError(null);
      setShowCreateModal(false);
      await refreshPools();
      await refreshCredentials();
    } catch (e: any) {
      const msg = e?.data?.message || e?.message || e?.error || 'Failed to create relay account';
      setModalError(String(msg));
    }
  }

  function openEditModal(pool: any) {
    const poolId = String(pool?.poolId || '');
    const provider = normalizeRelayProvider(String(pool?.providerType || ''));
    setEditingPoolId(poolId);
    setEditingProvider(provider);
    setEditName(displayPoolName(String(pool?.name || '')));
    setEditPurpose(purposeFromPool(pool));
    const suffix = extractRelaySuffix(pool);
    setEditSuffix(RELAY_ACCOUNT_SUFFIX_PATTERN.test(suffix) ? suffix : '');
    setEditCredentialUsername(
      providerUsesPoolLevelCredentials(provider) ? String(credentialsByPoolId[poolId]?.channel || '') : ''
    );
    setEditCredentialPassword('');
    setModalError(null);
    setError(null);
  }

  async function submitEditRelayAccount() {
    if (!editingPoolId) return;
    const name = String(editName || '').trim();
    const suffix = String(editSuffix || '').trim();
    const credentialUsername = String(editCredentialUsername || '').trim();
    const credentialPassword = String(editCredentialPassword || '').trim();
    const existingChannel = String(credentialsByPoolId[editingPoolId]?.channel || '').trim();
    const usePoolCredentials = providerUsesPoolLevelCredentials(editingProvider);
    if (!name) {
      setModalError('Relay account name is required.');
      return;
    }
    if (suffix && !RELAY_ACCOUNT_SUFFIX_PATTERN.test(suffix)) {
      setModalError('Relay account suffix must be exactly 4 letters/numbers (example: 083c).');
      return;
    }

    setModalError(null);
    setError(null);
    try {
      const updateRes: any = await updateSendPool({
        poolId: editingPoolId,
        name,
        purpose: relayPurposeToApiPurpose(editPurpose),
        relayAccountSuffix: suffix || '',
      }).unwrap();
      const updatePayload = updateRes?.data || updateRes || {};
      const updatedSuffix = String(updatePayload?.relayAccountSuffix || '').trim();
      const backendReturnedSuffix = RELAY_ACCOUNT_SUFFIX_PATTERN.test(updatedSuffix);
      // Back-compat fallback for stacks where relayAccountSuffix is not yet supported on pool meta.
      if (suffix && !backendReturnedSuffix) {
        await updateSendPool({
          poolId: editingPoolId,
          name: `${name} [${suffix}]`,
        }).unwrap();
      }
      const usernameChanged = Boolean(credentialUsername && credentialUsername !== existingChannel);
      const shouldSaveCredentials = usePoolCredentials && (usernameChanged || Boolean(credentialPassword));
      if (shouldSaveCredentials) {
        await putPoolCredentials({
          poolId: editingPoolId,
          username: usernameChanged ? credentialUsername : undefined,
          password: credentialPassword || undefined,
        }).unwrap();
      }
      setModalError(null);
      setEditingPoolId(null);
      await refreshPools();
      await refreshCredentials();
    } catch (e: any) {
      const msg = e?.data?.message || e?.message || e?.error || 'Failed to update relay account';
      setModalError(String(msg));
    }
  }

  const loading = isLoadingPools || isCreatingPool || isUpdatingPool || isSavingRelayCredentials;

  return (
    <>
      <EMMSectionHeader title="Relay Accounts" showHelp onHelpClick={() => setShowHelpSidebar(true)} />
      <div className="pt-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              className="d-flex align-items-center"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              onClick={openCreateModal}
              disabled={loading}
            >
              Add relay account
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="d-flex align-items-center"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              onClick={() => {
                refreshPools();
                refreshUsage();
                refreshCredentials();
              }}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>
        </div>

        <Card className="mb-3">
          <Card.Body className="py-2">
            <div className="text-muted small">
              Manage all relay accounts for this tenant in one place. Use the 4-character suffix to distinguish multiple
              accounts from the same provider (for example <code>MailChannels 1234</code> and <code>Twilio SendGrid 083c</code>).
            </div>
            <div className="text-muted small mt-1">
              Credential scope: <strong>MailChannels</strong>, <strong>Twilio SendGrid</strong>, and <strong>Elastic Email</strong> can be configured once here. <strong>SMTP.com</strong> and <strong>Mailgun</strong> are configured per domain in <strong>Domains - Outbound</strong>.
            </div>
            <div className="text-muted small mt-1">
              Elastic Email appears in this table after you create a relay account with provider set to <strong>Elastic Email</strong>, then save SMTP username + password.
            </div>
          </Card.Body>
        </Card>

        {error && (
          <Alert variant="danger" className="py-2">
            {error}
          </Alert>
        )}

        <Card>
          <Card.Header>Relay Accounts</Card.Header>
          <Card.Body style={{ padding: 0 }}>
            {loading ? (
              <div className="p-3 text-muted small">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading relay accounts...
              </div>
            ) : relayPools.length === 0 ? (
              <div className="p-3 text-muted small">
                No relay accounts found yet. Create your first relay account.
              </div>
            ) : (
              <Table responsive hover size="sm" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>Relay Account</th>
                    <th>Provider</th>
                    <th>Pool Name</th>
                    <th>Purpose</th>
                    <th>Credentials</th>
                    <th>Domains Using</th>
                    <th>Status</th>
                    <th>Health</th>
                    <th>Updated</th>
                    <th style={{ width: 120 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {relayPools.map((pool: any) => {
                    const poolId = String(pool.poolId || '');
                    const suffix = extractRelaySuffix(pool);
                    const alias = relayAlias(pool.providerType, suffix);
                    const usage = usageByPoolId[poolId] || { domainCount: 0, domains: [] };
                    const poolUsesSharedCredentials = providerUsesPoolLevelCredentials(pool.providerType);
                    const credentials = credentialsByPoolId[poolId];
                    const credentialsSet = Boolean(credentials?.configured);
                    const updatedAt = credentials?.lastUpdatedAt || pool?.updatedAt || null;
                    const previewDomains = usage.domains.slice(0, 2);
                    const remainingDomains = Math.max(0, usage.domainCount - previewDomains.length);
                    return (
                      <tr key={poolId}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{alias}</div>
                          <div className="small text-muted">{poolId}</div>
                          {!suffix && (
                            <Badge bg="warning" text="dark" className="mt-1">Missing 4-character tag</Badge>
                          )}
                        </td>
                        <td>{providerLabel(pool.providerType)}</td>
                        <td>{displayPoolName(pool.name || '') || '-'}</td>
                        <td>{purposeLabel(pool)}</td>
                        <td>
                          {poolUsesSharedCredentials ? (
                            <Badge bg={credentialsSet ? 'success' : 'secondary'}>
                              {credentialsSet ? 'Configured' : 'Missing'}
                            </Badge>
                          ) : (
                            <Badge bg="info">Per-domain</Badge>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{usage.domainCount}</div>
                          {previewDomains.length > 0 && (
                            <div className="small text-muted">
                              {previewDomains.join(', ')}
                              {remainingDomains > 0 ? ` +${remainingDomains} more` : ''}
                            </div>
                          )}
                        </td>
                        <td>{pool.status || 'UNKNOWN'}</td>
                        <td>
                          <Badge bg={healthVariant(pool?.health?.state)}>{pool?.health?.state || 'UNKNOWN'}</Badge>
                        </td>
                        <td className="small text-muted">
                          {updatedAt
                            ? (toLocaleStringInTimezone(
                                updatedAt,
                                'en-US',
                                { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' },
                                tenantTimezone
                              ) || String(updatedAt))
                            : '-'}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => openEditModal(pool)}
                            disabled={loading}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Relay Account</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalError && (
              <Alert variant="danger" className="py-2">
                {modalError}
              </Alert>
            )}
            <Row className="g-2">
              <Col md={12}>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  placeholder="e.g. MailChannels Primary"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Provider</Form.Label>
                <Form.Select
                  value={createProvider}
                  onChange={(e) => setCreateProvider(e.target.value as RelayProvider)}
                >
                  <option value="MAILCHANNELS">MailChannels</option>
                  <option value="SMTP_COM">SMTP.com</option>
                  <option value="MAILGUN">Mailgun</option>
                  <option value="SENDGRID">Twilio SendGrid</option>
                  <option value="ELASTIC_EMAIL">Elastic Email</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Purpose</Form.Label>
                <Form.Select
                  value={createPurpose}
                  onChange={(e) => setCreatePurpose(e.target.value as RelayPurpose)}
                >
                  <option value="cold_outreach">Cold Outreach</option>
                  <option value="transactional">Transactional</option>
                  <option value="newsletter">Newsletter</option>
                </Form.Select>
              </Col>
              <Col md={12}>
                <Form.Label>4-character relay account suffix</Form.Label>
                <Form.Control
                  value={createSuffix}
                  onChange={(e) => {
                    const next = normalizeRelaySuffixInput(e.target.value);
                    setCreateSuffix(next);
                  }}
                  placeholder="e.g. 083c"
                  inputMode="text"
                  maxLength={4}
                />
                <Form.Text className="text-muted">
                  This displays as <code>{relayAlias(createProvider, createSuffix || '083c')}</code>.
                </Form.Text>
              </Col>
              {providerUsesPoolLevelCredentials(createProvider) ? (
                <>
                  <Col md={12}>
                    <hr className="my-2" />
                    <div className="small fw-semibold mb-1">Relay credentials (optional)</div>
                  </Col>
                  {isSendgridProvider(createProvider) && (
                    <Col md={12}>
                      <Alert variant="info" className="py-2 mb-0">
                        Quick setup: for Twilio SendGrid, use username <code>apikey</code> and paste your SendGrid API key as the password.
                      </Alert>
                    </Col>
                  )}
                  <Col md={12}>
                    <Form.Label>{relayCredentialUsernameLabel(createProvider)}</Form.Label>
                    <Form.Control
                      value={createCredentialUsername}
                      onChange={(e) => setCreateCredentialUsername(e.target.value)}
                      placeholder={relayCredentialUsernamePlaceholder(createProvider)}
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>{relayCredentialPasswordLabel(createProvider)}</Form.Label>
                    <Form.Control
                      type="password"
                      value={createCredentialPassword}
                      onChange={(e) => setCreateCredentialPassword(e.target.value)}
                      placeholder={relayCredentialPasswordPlaceholder(createProvider)}
                    />
                    <Form.Text className="text-muted">{relayCredentialHint(createProvider)}</Form.Text>
                  </Col>
                </>
              ) : (
                <Col md={12}>
                  <Alert variant="info" className="py-2 mb-0">
                    {providerCredentialScopeText(createProvider)}
                  </Alert>
                </Col>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitCreateRelayAccount} disabled={isCreatingPool || isSavingRelayCredentials || !createName.trim()}>
              {isCreatingPool || isSavingRelayCredentials ? 'Saving...' : 'Create relay account'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={editingPoolId !== null} onHide={() => setEditingPoolId(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Relay Account</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalError && (
              <Alert variant="danger" className="py-2">
                {modalError}
              </Alert>
            )}
            <Row className="g-2">
              <Col md={12}>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  placeholder="Relay account name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </Col>
              <Col md={12}>
                <Form.Label>Purpose</Form.Label>
                <Form.Select
                  value={editPurpose}
                  onChange={(e) => setEditPurpose(e.target.value as RelayPurpose)}
                >
                  <option value="cold_outreach">Cold Outreach</option>
                  <option value="transactional">Transactional</option>
                  <option value="newsletter">Newsletter</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Transactional and Newsletter map to Business Email routing pools.
                </Form.Text>
              </Col>
              <Col md={12}>
                <Form.Label>4-character relay account suffix</Form.Label>
                <Form.Control
                  value={editSuffix}
                  onChange={(e) => {
                    const next = normalizeRelaySuffixInput(e.target.value);
                    setEditSuffix(next);
                  }}
                  placeholder="e.g. 083c"
                  inputMode="text"
                  maxLength={4}
                />
                <Form.Text className="text-muted">
                  Leave empty to remove the suffix.
                </Form.Text>
              </Col>
              {providerUsesPoolLevelCredentials(editingProvider) ? (
                <>
                  <Col md={12}>
                    <hr className="my-2" />
                    <div className="small fw-semibold mb-1">Relay credentials (optional)</div>
                  </Col>
                  {isSendgridProvider(editingProvider) && (
                    <Col md={12}>
                      <Alert variant="info" className="py-2 mb-0">
                        Quick setup: for Twilio SendGrid, use username <code>apikey</code> and paste your SendGrid API key as the password.
                      </Alert>
                    </Col>
                  )}
                  <Col md={12}>
                    <Form.Label>{relayCredentialUsernameLabel(editingProvider)}</Form.Label>
                    <Form.Control
                      value={editCredentialUsername}
                      onChange={(e) => setEditCredentialUsername(e.target.value)}
                      placeholder={relayCredentialUsernamePlaceholder(editingProvider)}
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>{relayCredentialPasswordLabel(editingProvider)}</Form.Label>
                    <Form.Control
                      type="password"
                      value={editCredentialPassword}
                      onChange={(e) => setEditCredentialPassword(e.target.value)}
                      placeholder={relayCredentialPasswordPlaceholder(editingProvider)}
                    />
                    <Form.Text className="text-muted">{relayCredentialHint(editingProvider)}</Form.Text>
                  </Col>
                </>
              ) : (
                <Col md={12}>
                  <Alert variant="info" className="py-2 mb-0">
                    {providerCredentialScopeText(editingProvider)}
                  </Alert>
                </Col>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditingPoolId(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitEditRelayAccount} disabled={isUpdatingPool || isSavingRelayCredentials || !editName.trim()}>
              {isUpdatingPool || isSavingRelayCredentials ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>

        <HelpSidebar
          pageKey="send-pools"
          isOpen={showHelpSidebar}
          onToggle={() => setShowHelpSidebar(!showHelpSidebar)}
        />
      </div>
    </>
  );
}

